import React, { useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import {
  allocation,
  Assignable,
  Identifiable,
  ProjectElementType,
  OptimizedGantt,
  Removable,
  Task as T,
} from "@planzone/dto";
import {
  useAssignTag,
  useAuthentication,
  useEditAllocation,
  useEditTask,
  useRetrieveConstraints,
} from "@planzone/view-models";
import { TaskCompletionStatus } from "@common/taskCompletionStatus";
import { useModal } from "@root/client/modal";
import { ActivityWatcher } from "@common/activityWatcher";
import { SecureContextualMenu } from "@common/secureContextualMenu";
import { ConstraintsPopup } from "@common/constraintsPopup";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { useIsElementHovered } from "@common/elementHover";
import { reCalculateDateRange } from "../utils";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { SecondaryCriteria } from "../secondaryCriteria";
import { Tags } from "../tags";
import { useProjectCtx } from "../projectCtx";
import { isBlocked } from "../utils";
import { DateForm } from "../dateForm";
import { TaskStatus } from "./taskStatus";
import { Props } from "./types";
import { AllocationUnit } from "@common/allocationUnit";
import { AllocationsList } from "./allocationsList";
import { NameEditionForm } from "./nameEditionForm";
import styles from "./styles.scss";

function toFixed(nbr: number) {
  return typeof nbr === "number" || !isNaN(parseFloat(nbr))
    ? Math.round(nbr) === nbr
      ? nbr
      : nbr.toFixed(2)
    : "";
}

function projectElementToTask(
  element: Extract<OptimizedGantt[number], { type: ProjectElementType.TASK }>,
): Identifiable<T> {
  // @ts-ignore
  return {
    ...element,
    uuid: element.uuid,
    name: element.name,
    description: element.description,
    startDate: element.startDate,
    endDate: element.endDate,
    createdDate: element.createdDate || "",
    completionDate: element.completionDate,
    percentComplete: element.percentComplete,
    allocations: element.allocations,
    status: element.status,
    tags: element.tags,
    activityKeyRank: element.referenceKeyRank,
    attributes: element.attributes,
    minStartDate: element.minStartDate ?? "",
    maxEndDate: element.maxEndDate ?? "",
  };
}

function Task({
  task,
  meta,
  parentList,
  isMultiProject,
  minDate,
  maxDate,
}: Props) {
  const { t } = useTranslation();
  const userTimezone = DateTime.local().zoneName;
  const realTask = projectElementToTask(task);
  const { project, template } = useProjectCtx();
  const projectTimezone = project.timeZone || userTimezone;
  const { constraints } = useRetrieveConstraints();
  const { assignTag } = useAssignTag();
  const { account } = useAuthentication();
  const isHovered = useIsElementHovered(task.uuid);
  const { editAllocation } = useEditAllocation({ ...allocation, uuid: "" });
  const isLate =
    !!task.endDate &&
    DateTime.fromISO(task.endDate, { zone: "utc" }) < DateTime.now();
  const isDone =
    task.percentComplete === 100 || task.status.percentComplete === 100;
  const userAllocation = task.allocations.find(
    (alloc) => alloc.resource.uuid === account.resource.uuid,
  );
  const { pushView } = useModal();
  const { isRequestPending, editTask } = useEditTask(realTask);
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const elementConstraints = constraints.filter(
    ({ targetEntityUuid, originEntityUuid }) =>
      targetEntityUuid === task.uuid || originEntityUuid === task.uuid,
  );
  const isConstrained = elementConstraints.length > 0;
  const blocked = isBlocked(task, elementConstraints);
  const recalculated = reCalculateDateRange(task, minDate, maxDate);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.task, {
        [styles.late]: isLate && !isDone,
        [styles.constrained]: blocked,
        [styles.hovered]: isHovered,
      })}
    >
      <SecondaryCriteria project={project}>
        {() => ({
          main: (
            <div
              className={styles.taskDetails}
              onClick={() =>
                pushView({
                  key: UseCases.retrieveTask,
                  data: { task: realTask, isMultiProject },
                })
              }
            >
              <div className={styles.name}>
                <div
                  className={styles.completionStatus}
                  onClick={
                    !project.isTemplate && !blocked
                      ? (e) => {
                          e.stopPropagation();
                          userAllocation && !isDone
                            ? task.allocations.length === 1
                              ? pushView({
                                  key: UseCases.markTaskAsCompleted,
                                  data: {
                                    task: realTask,
                                    project,
                                  },
                                })
                              : editAllocation({
                                  allocation: userAllocation,
                                  nextAllocation: {
                                    ...userAllocation,
                                    percentComplete:
                                      userAllocation.percentComplete !== 100
                                        ? 100
                                        : 0,
                                  },
                                })
                            : !isDone
                              ? pushView({
                                  key: UseCases.markTaskAsCompleted,
                                  data: {
                                    task: realTask,
                                    project,
                                  },
                                })
                              : editTask({
                                  task: realTask,
                                  nextTask: { ...realTask, percentComplete: 0 },
                                });
                        }
                      : undefined
                  }
                >
                  <TaskCompletionStatus
                    progress={isDone ? 100 : task.percentComplete}
                    endDate={task.endDate}
                    isDisabled={project.isTemplate}
                    isConstrained={blocked}
                    task={realTask}
                    resource={account.resource}
                  />
                </div>
                <NameEditionForm
                  task={realTask}
                  hovered={isHovered}
                  isConstrained={blocked}
                  isMultiProject={isMultiProject}
                />
              </div>
              {isConstrained && (
                <div
                  className={styles.constraintsPopup}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ConstraintsPopup
                    elementId={task.uuid}
                    elementType="task"
                    constraintsNbr={elementConstraints.length}
                    project={project}
                  />
                </div>
              )}
            </div>
          ),
          criteria: {
            statuses: (
              <div className={styles.taskStatus}>
                <TaskStatus task={realTask} project={project} />
              </div>
            ),
            tags: (
              <p className={styles.tags}>
                <Tags
                  tags={task.tags}
                  project={project}
                  removable={Removable.TASK}
                  uuid={task.uuid}
                  add={(tag) =>
                    assignTag({
                      tag,
                      assignable: Assignable.TASK,
                      assignableId: task.uuid,
                      project,
                    })
                  }
                />
              </p>
            ),
            allocations: (
              <div className={styles.allocations}>
                <AllocationsList
                  allocations={task.allocations}
                  task={realTask}
                  project={project}
                />
              </div>
            ),
            allocated: (
              <div className={styles.allocated}>
                {toFixed(meta.allocatedHours)}
                <AllocationUnit amount={meta.allocatedHours} format="symbol" />
              </div>
            ),
            spent: (
              <div className={styles.allocated}>
                {toFixed(meta.spentHours)}
                <AllocationUnit amount={meta.spentHours} format="symbol" />
              </div>
            ),
            startDate: (
              <div className={styles.endDate}>
                <DateForm
                  projectElement={task}
                  projectTimezone={projectTimezone}
                  date={task.startDate}
                  recalculatedDate={recalculated.startDate}
                  disabled={isRequestPending}
                  minDate={(() => {
                    const bound = minDate || task.minStartDate;
                    if (!bound) return undefined;
                    const dt = DateTime.fromISO(bound, {
                      zone: "utc",
                    }).setZone(userTimezone);
                    return dt.isValid
                      ? { year: dt.year, month: dt.month - 1, day: dt.day }
                      : undefined;
                  })()}
                  maxDate={(() => {
                    const bound = task.endDate || maxDate || task.maxEndDate;
                    if (!bound) return undefined;
                    const dt = DateTime.fromISO(bound, {
                      zone: "utc",
                    }).setZone(userTimezone);
                    return dt.isValid
                      ? { year: dt.year, month: dt.month - 1, day: dt.day }
                      : undefined;
                  })()}
                  submit={(startDate) =>
                    editTask({
                      task: realTask,
                      nextTask: { ...realTask, startDate },
                    })
                  }
                />
              </div>
            ),
            endDate: (
              <div className={styles.endDate}>
                <DateForm
                  projectElement={task}
                  projectTimezone={projectTimezone}
                  date={task.endDate}
                  recalculatedDate={recalculated.endDate}
                  disabled={isRequestPending}
                  minDate={(() => {
                    const bound =
                      task.startDate || minDate || task.minStartDate;
                    if (!bound) return undefined;
                    const dt = DateTime.fromISO(bound, {
                      zone: "utc",
                    }).setZone(userTimezone);
                    return dt.isValid
                      ? { year: dt.year, month: dt.month - 1, day: dt.day }
                      : undefined;
                  })()}
                  submit={(endDate) =>
                    editTask({
                      task: realTask,
                      nextTask: { ...realTask, endDate },
                    })
                  }
                />
              </div>
            ),
            completionDate: (
              <div className={styles.endDate}>
                {!!task.completionDate && (
                  <DateForm
                    projectElement={task}
                    projectTimezone={projectTimezone}
                    date={task.completionDate}
                    recalculatedDate=""
                    disabled={isRequestPending}
                    isCompletionDate={true}
                    submit={(date) => {
                      const nextDt = DateTime.fromISO(date, { zone: "utc" });
                      const nextIso = nextDt.toUTC().toISO() ?? "";
                      const currentDt = task.completionDate
                        ? DateTime.fromISO(task.completionDate, {
                            zone: "utc",
                          })
                        : null;
                      editTask({
                        task: realTask,
                        nextTask: {
                          ...realTask,
                          completionDate:
                            !!currentDt && nextDt.equals(currentDt)
                              ? ""
                              : nextIso,
                        },
                      });
                    }}
                  />
                )}
              </div>
            ),
            ...template.reduce(
              (acc, criteria) => ({
                ...acc,
                [criteria.uuid]: (
                  <SecondaryCriteriaForm
                    criteria={criteria}
                    value={
                      task.attributes
                        ? task.attributes[criteria.uuid] || ""
                        : ""
                    }
                    projectTimezone={projectTimezone}
                    disabled={isRequestPending}
                    submit={(value) =>
                      editTask({
                        task: realTask,
                        nextTask: {
                          ...realTask,
                          attributes: {
                            ...task.attributes,
                            [criteria.uuid]: value,
                          },
                        },
                      })
                    }
                  />
                ),
              }),
              {},
            ),
          },
          comments: (
            <CommentsCount
              subject={task.uuid}
              type="task"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount uuid={task.uuid} fileNbr={meta.attachmentCount} />
          ),
          contextualMenu: (
            <div
              className={classNames(styles.contextualMenu, {
                [styles.displayedContextualMenu]: showContextualMenu,
              })}
            >
              <div ref={ref}>
                <SecureContextualMenu
                  context={{ task: realTask }}
                  replacement={<div className={styles.placeholder} />}
                  items={[
                    {
                      name: t("projectPage.tasksContextualMenu.edit"),
                      useCase: UseCases.editTask,
                      act: () =>
                        pushView({
                          data: { task: realTask, isMultiProject },
                          key: UseCases.retrieveTask,
                        }),
                    },
                    {
                      name: t("projectPage.tasksContextualMenu.move"),
                      useCase: UseCases.moveListElement,
                      act: () =>
                        pushView({
                          key: UseCases.moveListElement,
                          data: {
                            projectElementId: task.uuid,
                            projectElementType: ProjectElementType.TASK,
                            currentParentList: parentList,
                            project,
                          },
                        }),
                    },
                    {
                      name: t("projectPage.tasksContextualMenu.duplicate"),
                      useCase: UseCases.createTask,
                      act: () =>
                        pushView({
                          data: { project, list: parentList, task: realTask },
                          key: UseCases.createTask,
                        }),
                    },
                    {
                      name: t("projectPage.tasksContextualMenu.remove"),
                      useCase: UseCases.removeTask,
                      color: "#db5f63",
                      act: () =>
                        pushView({
                          data: realTask,
                          key: UseCases.removeTask,
                        }),
                    },
                  ]}
                />
              </div>
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { Task };
